import {
    PaletteBackgroundBody,
    PaletteAccentBase,
    PaletteAccentContrastText,
    PaletteBackgroundContent,
    PaletteBackgroundOffset,
    PaletteBorder,
    PaletteDivider,
    PalettePrimaryBase,
    PalettePrimaryContrastText,
    PaletteSecondaryBase,
    PaletteSecondaryContrastText,
    PaletteStarRating,
    PaletteTertiaryBase,
    PaletteTertiaryContrastText,
    PaletteTextBody,
    PaletteTextHeading,
    PaletteTextLight,
    PaletteTextLink,
    PaletteTextPrice,
    TypographyFamilyBodyFontFamily,
    TypographyFamilyBodyFontWeightBold,
    TypographyFamilyBodyFontWeightDefault,
    TypographyFamilyBodyFontWeightSemiBold,
    TypographyFamilyHeadingFontFamily,
    TypographyFamilyHeadingFontWeightDefault,
    TypographyFamilyHeroFontFamily,
    TypographyFamilyHeroFontWeightDefault,
    TypographyFamilyHeroTextTransform,
    TypographyFamilyPriceFontFamily,
    TypographyFamilyPriceFontWeightDefault,
    TypographyFamilyPriceTextTransform,
    TypographyFamilyHeadingFontWeightBold,
} from '../design-tokens/dnata-travel-tokens';
import { HoneycombStyleSystemProps } from '../theming';
import { HoneycombThemeOverrides } from '../theming/types/HoneycombTheme';

const accentColor = {
    base: PaletteAccentBase,
    contrastText: PaletteAccentContrastText,
};

export const DNATA_TRAVEL_THEME_OVERRIDES: HoneycombThemeOverrides = {
    palette: {
        primary: {
            base: PalettePrimaryBase,
            contrastText: PalettePrimaryContrastText,
        },
        secondary: {
            base: PaletteSecondaryBase,
            contrastText: PaletteSecondaryContrastText,
        },
        tertiary: {
            base: PaletteTertiaryBase,
            contrastText: PaletteTertiaryContrastText,
        },
        accent: accentColor,
        accentPrimary: accentColor,
        accentSecondary: accentColor,
        accentTertiary: accentColor,
        text: {
            body: PaletteTextBody,
            light: PaletteTextLight,
            heading: PaletteTextHeading,
            price: PaletteTextPrice,
            link: PaletteTextLink,
        },
        background: {
            body: PaletteBackgroundBody,
            offset: PaletteBackgroundOffset,
            content: PaletteBackgroundContent,
        },
        starRating: PaletteStarRating,
        divider: PaletteDivider,
        border: PaletteBorder,
    },
    typography: {
        family: {
            body: {
                fontFamily: TypographyFamilyBodyFontFamily,
                fontWeight: {
                    default: TypographyFamilyBodyFontWeightDefault,
                    regular: TypographyFamilyBodyFontWeightDefault,
                    semiBold: TypographyFamilyBodyFontWeightSemiBold,
                    bold: TypographyFamilyBodyFontWeightBold,
                },
            },
            caption: {
                fontFamily: TypographyFamilyBodyFontFamily,
                fontWeight: {
                    default: TypographyFamilyBodyFontWeightDefault,
                    regular: TypographyFamilyBodyFontWeightDefault,
                    semiBold: TypographyFamilyBodyFontWeightSemiBold,
                    bold: TypographyFamilyBodyFontWeightBold,
                },
            },
            heading: {
                fontFamily: TypographyFamilyHeadingFontFamily,
                fontWeight: {
                    default: TypographyFamilyHeadingFontWeightDefault,
                    bold: TypographyFamilyHeadingFontWeightBold,
                },
            },
            hero: {
                fontFamily: TypographyFamilyHeroFontFamily,
                fontWeight: {
                    default: TypographyFamilyHeroFontWeightDefault,
                },
                textTransform: TypographyFamilyHeroTextTransform,
            },
            price: {
                fontFamily: TypographyFamilyPriceFontFamily,
                fontWeight: {
                    default: TypographyFamilyPriceFontWeightDefault,
                    regular: 400,
                    semiBold: 600,
                    bold: 700,
                },
                textTransform: TypographyFamilyPriceTextTransform,
            },
        },
    },
    components: {
        defaultProps: {
            Paper: {
                // Disable the border radius
                cornerStyle: 'square',
                border: 0,
            },
            Gallery: {
                // Disable the border radius
                cornerStyle: 'square',
            },
            Button: {
                cornerStyle: 'pill',
            },
            IconButton: {
                shape: 'rounded',
            },
            AutocompleterCategory: {
                cornerStyle: 'rounded',
            },
            Accordion: {
                bordercolor: PalettePrimaryBase,
            },
            Dialog: {
                cornerStyle: 'rounded',
            },
            Popup: {
                borderWidth: 1,
            },
            Divider: {
                thickness: 2,
            },
            Notification: {
                cornerStyle: 'rounded',
            },
            Stamp: {
                p: 3,
                pr: 4,
                pb: 4,
                bbrr: 24,
            },
            Flag: {
                br: '0 0 12px 0',
            },
            Option: {
                cornerStyle: 'rounded',
            },
            ProgressBar: {
                color: 'primary.base',
            },
        },
        themes: {
            Button: {
                colors: {
                    primary: {
                        background: 'primary.base',
                        border: 'primary.base',
                        textOutlined: 'primary.base',
                        textFilled: 'common.white',
                    },
                    secondary: {
                        background: 'accent.base',
                        border: 'accent.base',
                        textOutlined: 'accent.base',
                        textFilled: 'common.white',
                    },
                    callToAction: {
                        background: 'primary.base',
                        border: 'primary.base',
                        textOutlined: 'primary.base',
                        textFilled: 'common.white',
                    },
                },
                fontWeight: 'semiBold',
            },

            Chip: {
                colors: {
                    tertiary: {
                        textFilled: 'common.white',
                        textOutlined: 'accent.base',
                    },
                },
            },
            Flag: {
                colors: {
                    primary: {
                        background: 'accent.base',
                        text: 'common.white',
                    },
                    secondary: {
                        background: 'common.white',
                        text: 'common.black',
                    },
                    tertiary: {
                        background: 'common.white',
                        text: 'common.black',
                    },
                },
            },
            Stamp: {
                // All themeable colour options currently default to TR orange
                colors: {
                    primary: {
                        background: 'accent.base',
                        text: 'common.white',
                    },
                    secondary: {
                        background: 'accent.base',
                        text: 'common.white',
                    },
                    tertiary: {
                        background: 'accent.base',
                        text: 'common.white',
                    },
                },
                radius: 0,
                typographyVariant: 'heading.xs',
            },
            Score: {
                size: {
                    xs: {
                        radius: '50% 0 50% 50%',
                        padding: '0 0 1px 0',
                    },
                    s: {
                        radius: '50% 0 50% 50%',
                        padding: '0 0 2px 0',
                    },
                    m: {
                        radius: '50% 0 50% 50%',
                        padding: '0 0 3px 0',
                    },
                    l: {
                        radius: '50% 0 50% 50%',
                        padding: '0 0 4px 0',
                    },
                },
                colors: {
                    primary: {
                        background: 'accent.base',
                        text: 'common.white',
                    },
                },
            },
            Tabs: {
                variant: {
                    default: {
                        underlineRounded: false,
                    },
                    hero: {
                        underlineRounded: false,
                        size: {
                            s: {
                                typographyVariant: 'hero.s',
                                padding: {
                                    x: 40,
                                    y: 16,
                                },
                                spacing: 0,
                            },
                            m: {
                                typographyVariant: 'hero.m',
                                padding: {
                                    x: 40,
                                    y: 16,
                                },
                                spacing: 0,
                            },
                            l: {
                                typographyVariant: 'hero.m',
                                padding: {
                                    x: 40,
                                    y: 16,
                                },
                                spacing: 0,
                            },
                        },
                    },
                },
            },
            Carousel: {
                colors: {
                    primary: {
                        dotsBackground: 'secondary.base',
                        dotsButtons: 'common.white',
                    },
                    secondary: {
                        dotsBackground: 'secondary.base',
                        dotsButtons: 'common.white',
                    },
                    tertiary: {
                        dotsBackground: 'accent.base',
                        dotsButtons: 'common.white',
                    },
                },
            },
            ActionButton: {
                colors: {
                    primary: {
                        content: 'secondary.base',
                        hoverBackground: 'grey.base',
                    },
                    secondary: {
                        content: 'primary.base',
                        hoverBackground: 'grey.base',
                    },
                    tertiary: {
                        content: 'accent.base',
                        hoverBackground: 'grey.base',
                    },
                },
            },
            Checkbox: {
                colors: {
                    checked: 'primary.base',
                },
            },
        },
    },
};

export const DNATA_TRAVEL_CUSTOM_STYLE_PROPS: {
    [key: string]: HoneycombStyleSystemProps;
} = {
    'price-block-card-image': {
        bbrr: '40px',
        overflow: 'hidden',
    },
    'merch-card-full-width': {
        bbrr: '112px',
        overflow: 'hidden',
    },
    'merch-card': {
        bbrr: '48px',
        overflow: 'hidden',
    },
    'email-signup-heading-panel': {
        bg: { xs: 'secondary.base', l: 'primary.base' },
    },
    'email-signup-form-panel': {
        bg: 'secondary.base',
    },
    'deal-card-section': {
        bc: 'primary.base',
    },
};
